/**
 * Mention Parser - Extraction Functions
 *
 * Functions for extracting mentions from text.
 */

import {
  STRUCTURED_MENTION_REGEX,
  STRUCTURED_MENTION_REGEX_TEST,
  LEGACY_MENTION_REGEX,
  ANY_MENTION_REGEX_TEST,
} from "./regex.js";

/**
 * Extract @mentions from text.
 *
 * Parses BOTH formats:
 *  - Structured tokens: @[Display Name](userId) → returns displayName (without @[])
 *  - Legacy: @username → returns username (without @)
 *
 * Structured tokens are extracted first and removed from the text so that
 * legacy regex doesn't accidentally match fragments inside them.
 *
 * @param {string} text - Input text containing @mentions
 * @returns {string[]} Array of unique mention identifiers (usernames or display names, without @ prefix)
 */
export function extractMentions(text) {
  if (!text || typeof text !== "string") {
    return [];
  }

  const mentions = new Set();

  // ── Step 1: Extract structured tokens first ──
  // We extract these first and strip them from the text so the legacy regex
  // below doesn't accidentally match on fragments inside the token.
  const structuredMatches = text.match(STRUCTURED_MENTION_REGEX);
  if (structuredMatches) {
    for (const match of structuredMatches) {
      // match = "[Display Name](userId)" — extract the display name
      const innerMatch = match.match(STRUCTURED_MENTION_REGEX_TEST);
      if (innerMatch) {
        mentions.add(innerMatch[1].toLowerCase());
      }
    }
  }

  // ── Step 2: Strip structured tokens, then extract legacy mentions ──
  const textWithoutStructured = text.replace(STRUCTURED_MENTION_REGEX, "");
  const legacyMatches = textWithoutStructured.match(LEGACY_MENTION_REGEX);
  if (legacyMatches) {
    for (const match of legacyMatches) {
      mentions.add(match.substring(1).toLowerCase());
    }
  }

  return [...mentions];
}

/**
 * Check if text contains any @mentions (structured or legacy).
 * @param {string} text - Input text
 * @returns {boolean} True if text contains at least one @mention
 */
export function hasMentions(text) {
  if (!text || typeof text !== "string") {
    return false;
  }
  return ANY_MENTION_REGEX_TEST.test(text);
}

/**
 * Extract structured mention tokens from text and return their data directly.
 * Structured tokens embed the userId, so no Firestore lookup is needed.
 *
 * @param {string} text - Text that may contain @[DisplayName](userId) tokens
 * @returns {Array<{displayName: string, userId: string}>}
 */
export function extractStructuredMentions(text) {
  if (!text || typeof text !== "string") {
    return [];
  }

  const results = [];
  // Use exec() loop to get all matches
  const regex = new RegExp(STRUCTURED_MENTION_REGEX.source, "g");
  let match;
  while ((match = regex.exec(text)) !== null) {
    results.push({
      displayName: match[1],
      userId: match[2],
    });
  }
  return results;
}
